import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import {
  Milestone,
  MilestoneIcon,
  Milestones,
  MilestoneStatus,
  MilestoneTitle,
} from "@/shared/service-manager/default-ui/process-page/process-page-overview";
import { formatDate, isSetDate } from "@/shared/utilities/date";
import { useGetDeliveryByProcessQuery } from "@/services/backend/deliveries/delivery/service";
import {
  ArtefactIconCounting,
  ArtefactIconDelivery,
  ArtefactIconDifferentiation,
  ArtefactIconDocumentation,
  ArtefactIconInspection,
} from "@/shared/components/domain/artefact/artefact-icon";
import { useListDocumentationsQuery } from "@/services/backend/documentations/service";
import { useListCountingsQuery } from "@/services/backend/countings/service";
import { useListDifferentiationsQuery } from "@/services/backend/differentiation/service";
import { useListInspectionsQuery } from "@/services/backend/inspections/service";
import { FilterMode } from "@/shared/lib/filter/filter";
import { useGetInspectionRulesQuery } from "@/services/backend/rules/service";

export function InspectionMilestones({ processId }: { processId: string }) {
  const { data: rule } = useGetInspectionRulesQuery({ processId: processId! });
  if (!rule) {
    return null;
  }

  return (
    <Card className="pb-0">
      <CardHeader>
        <CardTitle>{t("Meilensteine")}</CardTitle>
        <CardDescription>
          {t(
            "Diese Ansicht bietet einen Überblick über den aktuellen Zustand des Prozesses",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Milestones>
          <DeliveryMilestone processId={processId!} />
          <DocumentationMilestone processId={processId} />
          <CountingMilestone processId={processId} />
          {rule.hasDifferentiation && (
            <DifferentiationMilestone processId={processId} />
          )}
          <InspectionMilestone processId={processId} />
        </Milestones>
      </CardContent>
    </Card>
  );
}

function DeliveryMilestone({ processId }: { processId: string }) {
  const { data } = useGetDeliveryByProcessQuery({
    processId: processId!,
  });
  const done = isSetDate(data?.immutabilitySetAt);

  return (
    <Milestone>
      <MilestoneIcon icon={ArtefactIconDelivery} done={done} />
      <MilestoneTitle>{t("Lieferung")}</MilestoneTitle>
      {done ? (
        <MilestoneStatus>
          {`Die Lieferung wurde am ${formatDate(data!.immutabilitySetAt!)} bearbeitet`}
        </MilestoneStatus>
      ) : (
        <MilestoneStatus>
          Die Lieferung wartet auf die Bearbeitung
        </MilestoneStatus>
      )}
    </Milestone>
  );
}

function DocumentationMilestone({ processId }: { processId: string }) {
  const { data } = useListDocumentationsQuery({
    processId: { active: true, values: [processId!] },
    ownerId: { active: true, values: [""], mode: FilterMode.IsNull },
    ownerType: { active: true, values: [""], mode: FilterMode.IsNull },
    limit: 0,
    offset: 0,
  });

  const doc = data?.items[data.items.length - 1];
  const done = isSetDate(doc?.immutabilitySetAt);

  return (
    <Milestone>
      <MilestoneIcon icon={ArtefactIconDocumentation} done={done} />
      <MilestoneTitle>{t("Dokumentation")}</MilestoneTitle>
      {done ? (
        <MilestoneStatus>
          {`Die Dokumentation wurde am ${formatDate(doc!.immutabilitySetAt!)} abgeschlossen.`}
        </MilestoneStatus>
      ) : (
        doc && (
          <MilestoneStatus>
            Die Dokumentation ist in Bearbeitung.
          </MilestoneStatus>
        )
      )}
    </Milestone>
  );
}

function CountingMilestone({ processId }: { processId: string }) {
  const { data } = useListCountingsQuery({
    processId: { active: true, values: [processId!] },
    ownerId: { active: true, values: [""], mode: FilterMode.IsNull },
    ownerType: { active: true, values: [""], mode: FilterMode.IsNull },
    limit: 0,
    offset: 0,
  });

  const doc = data?.items[data.items.length - 1];
  const done = isSetDate(doc?.immutabilitySetAt);

  return (
    <Milestone>
      <MilestoneIcon icon={ArtefactIconCounting} done={done} />
      <MilestoneTitle>{t("Zählung")}</MilestoneTitle>
      {done ? (
        <MilestoneStatus>
          {`Die Zählung wurde am ${formatDate(doc!.immutabilitySetAt!)} abgeschlossen.`}
        </MilestoneStatus>
      ) : (
        doc && (
          <MilestoneStatus>
            {isSetDate(doc.processStartedAt)
              ? `Die Zählung wurde am ${formatDate(doc.processStartedAt!)} gestartet.`
              : "Die Zählung ist in Bearbeitung."}
          </MilestoneStatus>
        )
      )}
    </Milestone>
  );
}

function DifferentiationMilestone({ processId }: { processId: string }) {
  const { data } = useListDifferentiationsQuery({
    processId: { active: true, values: [processId!] },
    ownerId: { active: true, values: [""], mode: FilterMode.IsNull },
    ownerType: { active: true, values: [""], mode: FilterMode.IsNull },
    limit: 0,
    offset: 0,
  });

  const doc = data?.items[data.items.length - 1];
  const done = isSetDate(doc?.immutabilitySetAt);

  return (
    <Milestone>
      <MilestoneIcon icon={ArtefactIconDifferentiation} done={done} />
      <MilestoneTitle>{t("Differenzierung")}</MilestoneTitle>
      {done ? (
        <MilestoneStatus>
          {`Die Differenzierung wurde am ${formatDate(doc!.immutabilitySetAt!)} abgeschlossen.`}
        </MilestoneStatus>
      ) : (
        doc && (
          <MilestoneStatus>
            {isSetDate(doc.processStartedAt)
              ? `Die Differenzierung wurde am ${formatDate(doc.processStartedAt!)} gestartet.`
              : "Die Differenzierung ist in Bearbeitung."}
          </MilestoneStatus>
        )
      )}
    </Milestone>
  );
}

function InspectionMilestone({ processId }: { processId: string }) {
  const { data } = useListInspectionsQuery({
    processId,
    limit: 0,
    offset: 0,
  });

  const insp = data?.items[data.items.length - 1];
  const done = isSetDate(insp?.immutabilitySetAt);

  return (
    <Milestone>
      <MilestoneIcon icon={ArtefactIconInspection} done={done} />
      <MilestoneTitle>{t("Prüfbericht")}</MilestoneTitle>
      {done ? (
        <MilestoneStatus>
          {`Der Prüfbericht wurde am ${formatDate(insp!.immutabilitySetAt!)} abgeschlossen.`}
        </MilestoneStatus>
      ) : (
        insp && (
          <MilestoneStatus>
            {isSetDate(insp.processStartedAt)
              ? `Der Prüfbericht wurde am ${formatDate(insp.processStartedAt!)} gestartet.`
              : "Der Prüfbericht ist in Bearbeitung."}
          </MilestoneStatus>
        )
      )}
    </Milestone>
  );
}
